<script setup lang="ts">
import { computed, ref, toRaw, unref, watch } from 'vue';
import X from '@assets/icons/close.svg';
import GPS from '@assets/icons/gps.svg';
import useJobContext from '@scripts/composables/useJobContext';
import { useJobSearchStore } from '@scripts/stores/jobSearchStore';
import {
    ComboboxAnchor,
    ComboboxContent,
    ComboboxEmpty,
    ComboboxGroup,
    ComboboxInput,
    ComboboxItem,
    ComboboxLabel,
    ComboboxRoot,
    ComboboxViewport,
    ScrollAreaRoot,
    ScrollAreaScrollbar,
    ScrollAreaThumb,
    ScrollAreaViewport,
    useFilter,
} from 'reka-ui';
import type { JobSearchOptions } from '@/types';

const props = defineProps<{
    onRequestGeolocation: () => Promise<boolean>;
}>();

const emits = defineEmits<{
    updateLocation: [location: string | undefined];
    updateTag: [];
    search: [];
}>();

const [injectContext] = useJobContext();
const { options, getLabel } = injectContext();

const { searchState } = useJobSearchStore();
const open = ref(false);

const { startsWith } = useFilter({ sensitivity: 'base' });
const filteredOptions = computed<JobSearchOptions>(() => {
    const tagKeys = Object.keys(searchState.tags);

    // remove all groups which are set as a tag
    const selectableOptions = Object.entries(options)
        .filter(([key]) => !tagKeys.includes(key))
        .map(([key, properties]) => {
            const matchingChildren = Object.entries(properties.children).filter(
                ([_, name]) => startsWith(name, searchState.text)
            );

            if (!matchingChildren.length) {
                return null;
            }

            return [
                key,
                {
                    ...properties,
                    children: Object.fromEntries(matchingChildren),
                },
            ];
        })
        .filter(
            (
                entry
            ): entry is [
                string,
                { name: string; children: Record<string, string> },
            ] => entry !== null
        );

    return Object.fromEntries(selectableOptions);
});

const comboModel = ref([]);

const hasOptions = computed(() => {
    const optionsCount = Object.keys(options).length;
    const tagsCount = Object.keys(searchState.tags).length;

    return optionsCount > tagsCount;
});

const selectedLocation = computed(() => {
    if (!searchState.tags.location) return null;

    return searchState.tags.location;
});

const selectedTag = ref<[string, string]>();

watch(
    () => searchState.tags?.location,
    (id) => {
        emits('updateLocation', id);
    }
);

function removeTag(optionKey: string, optionValue: string) {
    if (optionValue === '__geolocate') {
        props.onRequestGeolocation();
    }

    comboModel.value = comboModel.value.filter(
        (value) => value !== optionValue
    );

    delete searchState.tags[optionKey];

    if (optionValue !== '__geolocate') {
        emits('updateTag');
    }
}

function onSelect(key: string, value: string, label: string) {
    searchState.tags[key] = value;
    if (label.toLowerCase().includes(searchState.text.toLocaleLowerCase())) {
        searchState.text = '';
    }

    emits('updateTag');
}

async function onSelectGeolocation() {
    const success = await props.onRequestGeolocation();

    if (success) {
        searchState.tags.location = '__geolocate';
    }
}

function onInput(event: KeyboardEvent) {
    const target = event.target as HTMLInputElement;
    const tags: [string, string][] = Array.from(
        Object.entries(searchState.tags)
    );

    if (!tags.length && event.key !== 'Enter') {
        return;
    } else if (!tags.length && event.key === 'Enter') {
        emits('search');
        open.value = false;
    }

    const lastTag = toRaw(tags.at(-1));

    switch (event.key) {
        case 'Delete':
        case 'Backspace':
            if (target.selectionStart !== 0 || target.selectionEnd !== 0) break;

            if (selectedTag.value) {
                const index = tags.findIndex(
                    (tag) => tag === selectedTag.value
                );
                removeTag(...selectedTag.value);
                selectedTag.value =
                    selectedTag.value === lastTag
                        ? tags.at(index - 1)
                        : tags.at(index + 1);
            } else if (event.key === 'Backspace') {
                selectedTag.value = lastTag;
                event.preventDefault();
            }
            break;
        case 'Home':
        case 'End':
        case 'ArrowRight':
        case 'ArrowLeft':
            const isArrowRight = event.key === 'ArrowRight';
            const isArrowLeft = !isArrowRight;

            if (target.selectionStart !== 0 || target.selectionEnd !== 0) {
                break;
            }

            // if you press ArrowLeft, then we last tag
            if (isArrowLeft && !selectedTag.value) {
                selectedTag.value = lastTag;
                event.preventDefault();
            }
            // if you press ArrowRight on last tag, you deselect
            else if (
                isArrowRight &&
                lastTag &&
                selectedTag.value?.[0] === lastTag[0] &&
                selectedTag.value?.[1] === lastTag[1]
            ) {
                selectedTag.value = undefined;
                event.preventDefault();
            } else if (selectedTag.value) {
                let index = tags.findIndex(
                    (tag) =>
                        tag[0] === selectedTag.value?.[0] &&
                        tag[1] === selectedTag.value?.[1]
                );

                if (isArrowLeft) {
                    index--;
                } else if (isArrowRight) {
                    index++;
                }

                if (tags[index]) {
                    selectedTag.value = unref(tags[index]);
                }

                event.preventDefault();
            }
            break;
        case 'ArrowUp':
        case 'ArrowDown':
            if (selectedTag.value) {
                event.preventDefault();
            }
            break;
        default:
            selectedTag.value = undefined;
    }
}

defineExpose({
    searchState,
    selectedLocation,
});
</script>

<template>
    <ComboboxRoot
        class="relative flex"
        :open-on-click="true"
        :open-on-focus="true"
        :reset-search-term-on-blur="false"
        :highlight-on-hover="false"
        :multiple="true"
        :ignore-filter="true"
        v-model:open="open"
    >
        <ComboboxAnchor
            class="flex w-full items-center justify-between md:min-w-[320px]"
        >
            <div
                class="border-primary flex min-h-full w-full flex-wrap items-center gap-2 border-2 p-5"
            >
                <div
                    v-for="(value, key) in searchState.tags"
                    :key="key"
                    class="bg-primary text-primary-foreground flex items-center justify-center gap-1 rounded px-3 font-medium"
                    :class="{
                        '!bg-black !text-white':
                            selectedTag &&
                            selectedTag[0] === key &&
                            selectedTag[1] === value,
                    }"
                >
                    <template v-if="value === '__geolocate'">
                        <GPS class="size-4 fill-current" />
                        <span class="text-xs">Mein Standort</span>
                    </template>

                    <span
                        class="text-xs"
                        v-else
                    >
                        {{ getLabel(key, value) }}
                    </span>
                    <button
                        class="p-0.5"
                        @click="removeTag(key, value)"
                    >
                        <X class="size-2.5" />
                    </button>
                </div>
                <ComboboxInput
                    class="-my-5 min-h-full flex-1 py-5 text-base font-bold focus:outline-0"
                    placeholder="Stellenangebote durchsuchen"
                    @keydown="onInput"
                    v-model="searchState.text"
                />
            </div>
        </ComboboxAnchor>
        <ComboboxContent
            v-if="hasOptions"
            class="bg-background border-primary absolute top-full z-20 mt-[-2px] w-full overflow-hidden rounded-b border-2 shadow-sm will-change-[opacity,transform] md:min-w-[320px]"
        >
            <ComboboxViewport class="divide-y-2 divide-neutral-200">
                <ComboboxEmpty
                    class="text-foreground/50 p-5 text-center text-sm"
                    v-if="searchState.text !== ''"
                >
                    Absenden, um nach
                    <b>„{{ searchState.text }}“</b> zu suchen
                </ComboboxEmpty>

                <ComboboxGroup
                    v-for="(group, key) in filteredOptions"
                    :key="group.name"
                    tabindex="-1"
                >
                    <ScrollAreaRoot>
                        <ScrollAreaViewport class="p-5">
                            <ComboboxLabel
                                class="sticky left-0 mb-3 w-fit text-xs/none font-bold uppercase"
                            >
                                {{ group.name }}
                            </ComboboxLabel>

                            <div
                                class="flex gap-2.5"
                                role="presentation"
                            >
                                <ComboboxItem
                                    v-if="
                                        key === 'location' &&
                                        searchState.text === ''
                                    "
                                    value="__geolocate"
                                    class="hover:bg-primary hover:text-primary-foreground data-[highlighted]:bg-primary data-[highlighted]:text-primary-foreground relative inline-flex cursor-pointer items-center rounded-full bg-neutral-100 px-3 py-2 text-sm leading-none whitespace-nowrap !shadow-none select-none active:bg-black active:text-white"
                                    @select="onSelectGeolocation"
                                >
                                    <GPS class="mr-1 size-4 fill-current" />
                                    <span>Mein Standort</span>
                                </ComboboxItem>

                                <ComboboxItem
                                    v-for="[value, name] in Object.entries(
                                        group.children
                                    ).sort(([, nameA], [, nameB]) =>
                                        nameA.localeCompare(nameB)
                                    )"
                                    :key="value"
                                    :value="value"
                                    class="hover:bg-primary hover:text-primary-foreground data-[highlighted]:bg-primary data-[highlighted]:text-primary-foreground relative inline-flex cursor-pointer items-center rounded-full bg-neutral-100 px-3 py-2 text-sm leading-none whitespace-nowrap select-none active:bg-black active:text-white"
                                    @select="() => onSelect(key, value, name)"
                                >
                                    <span>{{ name }}</span>
                                </ComboboxItem>

                                <span
                                    class="w-5"
                                    aria-hidden="true"
                                    >&nbsp;</span
                                >
                            </div>
                        </ScrollAreaViewport>
                        <ScrollAreaScrollbar
                            class="bg-background flex touch-none p-0.5 transition-colors duration-[160ms] ease-out select-none hover:bg-neutral-100 data-[orientation=horizontal]:h-2.5 data-[orientation=horizontal]:flex-col data-[orientation=vertical]:w-2.5"
                            orientation="horizontal"
                        >
                            <ScrollAreaThumb
                                class="relative flex-1 rounded-[10px] bg-neutral-300 before:absolute before:top-1/2 before:left-1/2 before:h-full before:min-h-[44px] before:w-full before:min-w-[44px] before:-translate-x-1/2 before:-translate-y-1/2 before:content-['']"
                            />
                        </ScrollAreaScrollbar>
                    </ScrollAreaRoot>
                </ComboboxGroup>
            </ComboboxViewport>
        </ComboboxContent>
    </ComboboxRoot>
</template>
